import { useEffect, useRef } from 'react'
import heroImg from './assets/images/hero_rainbow.png'
import texturePalace from './assets/images/texture_palace.png'
import founderPortrait from './assets/images/founder_portrait.jpg'
import handshakeLuxury from './assets/images/handshake_luxury.png'
import palaisBuilding from './assets/images/palais_building.png'
import palaisExterior from './assets/images/palais_exterior.png'
import systemDesk from './assets/images/system_desk.png'
import symbolGold from './assets/images/symbol_gold.png'
import monogramAH from './assets/images/monogram_AH.png'

function useReveal() {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible')
          }
        })
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    )

    const elements = ref.current?.querySelectorAll('.section-reveal')
    elements?.forEach((el) => observer.observe(el))

    return () => observer.disconnect()
  }, [])

  return ref
}

export default function App() {
  const containerRef = useReveal()

  return (
    <div ref={containerRef} className="relative bg-luxblack">
      {/* Fixed watermark */}
      <img
        src={symbolGold}
        alt=""
        className="watermark fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] object-contain z-0"
      />

      {/* ═══════════════ HERO ═══════════════ */}
      <section className="relative min-h-[120vh] flex items-center justify-center overflow-hidden">
        {/* Hero Rainbow Image - THE HOUSE SYMBOL - FULL PROMINENCE */}
        <img
          src={heroImg}
          alt="AH Brandhouse Symbol"
          className="absolute inset-0 w-full h-full object-cover hero-symbol"
        />
        
        {/* Dramatic overlays */}
        <div className="gradient-overlay-hero absolute inset-0" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-transparent to-black/30" />
        
        {/* Gold border frame */}
        <div className="absolute inset-6 md:inset-12 border border-gold/20 pointer-events-none" />
        <div className="absolute inset-8 md:inset-14 border border-gold/10 pointer-events-none" />

        {/* Monogram top - PROMINENT with glow */}
        <div className="absolute top-12 left-1/2 -translate-x-1/2 z-20">
          <img src={monogramAH} alt="AH" className="h-20 md:h-24 monogram-glow animate-fade-in" />
        </div>

        {/* Gold top line */}
        <div className="absolute top-0 left-0 right-0 h-1 shimmer-gold" />

        <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
          <p className="text-gold/80 text-xs md:text-sm tracking-mega uppercase mb-10 font-sans animate-fade-in">
            كتيب خاص · MMXXVI
          </p>

          <h1 className="font-serif text-5xl md:text-7xl lg:text-9xl font-bold tracking-wider mb-8 animate-fade-in text-shadow-luxury leading-none">
            <span className="gold-gradient-text">AH BRANDHOUSE</span>
          </h1>

          <div className="gold-line-thick w-48 mx-auto my-10 animate-fade-in-slow" />

          <p className="text-gold-light font-serif text-2xl md:text-3xl lg:text-4xl mb-8 animate-fade-in-slow leading-relaxed" style={{ animationDelay: '0.4s' }}>
            الأنظمة الحسية<br />للضيافة الفاخرة
          </p>

          <div className="mt-14 space-y-4 animate-fade-in-slow" style={{ animationDelay: '0.8s' }}>
            <p className="text-white/90 font-sans text-base md:text-lg leading-loose tracking-wide">
              نحن لا نصمم الحملات.
            </p>
            <p className="text-white/90 font-sans text-base md:text-lg leading-loose tracking-wide">
              نحن نصمم كيف يُتذكر المكان.
            </p>
          </div>

          <div className="mt-20 animate-fade-in-slow" style={{ animationDelay: '1.2s' }}>
            <div className="gold-line w-32 mx-auto mb-8" />
            <p className="text-gold/60 text-[12px] tracking-luxe font-sans uppercase">
              For those who understand that perception is the real layer of value.
            </p>
          </div>
        </div>

        {/* Bottom gold accent */}
        <div className="absolute bottom-0 left-0 right-0 h-px shimmer-gold" />
      </section>

      {/* ═══════════════ PHILOSOPHY ═══════════════ */}
      <section className="relative bg-luxblack py-36 md:py-52 overflow-hidden">
        {/* Gold corner accents */}
        <div className="absolute top-0 left-0 w-32 h-32 border-t-2 border-l-2 border-gold/30" />
        <div className="absolute top-0 right-0 w-32 h-32 border-t-2 border-r-2 border-gold/30" />
        <div className="absolute bottom-0 left-0 w-32 h-32 border-b-2 border-l-2 border-gold/30" />
        <div className="absolute bottom-0 right-0 w-32 h-32 border-b-2 border-r-2 border-gold/30" />

        <img
          src={texturePalace}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-[0.03]"
        />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-start">
            <div className="section-reveal">
              <p className="text-gold text-xs tracking-mega uppercase mb-8 font-sans">
                ══ الأساس ══
              </p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight mb-8">
                <span className="gold-gradient-text">كيف يصبح الإدراك ذاكرة.</span>
              </h2>
              <p className="font-serif text-gold-light/80 text-xl md:text-2xl italic mb-12">
                العلامة التجارية لا تُرى. تُتذكَّر.
              </p>
              <div className="gold-line w-24 mt-8" />
            </div>

            <div className="section-reveal space-y-8" style={{ transitionDelay: '0.3s' }}>
              <p className="text-white/80 font-sans text-[15px] leading-[2.2]">
                الرائحة تصل إلى الذاكرة قبل المنطق.<br />
                تُشفّر العاطفة مباشرةً في المسارات العصبية،<br />
                أسرع من الهوية البصرية،<br />
                وأكثر استمراراً من الرسائل.
              </p>

              <p className="text-white/80 font-sans text-[15px] leading-[2.2]">
                كل وصول ضيف هو حدث عصبي.<br />
                الهواء الذي يتنفسونه،<br />
                الحرارة التي يحسونها،<br />
                الترددات البيئية التي يشعرون بها،<br />
                هذه الإشارات تتجاوز التقييم العقلاني<br />
                وتُكتب مباشرةً في الذاكرة العاطفية.
              </p>

              <p className="text-white/80 font-sans text-[15px] leading-[2.2]">
                عقار يفهم هذا لا يسوق نفسه.<br />
                يُبصم نفسه.
              </p>

              <p className="text-gold font-serif text-lg mt-12 italic border-r-2 border-gold/50 pr-6">
                Brandhouse AH بُنيت على هذه الفرضية.
              </p>
            </div>
          </div>

          <div className="gold-line w-full mt-24" />
          <p className="section-reveal mt-12 text-center text-gold/50 text-[12px] tracking-luxe font-sans" style={{ transitionDelay: '0.6s' }}>
            What is felt is remembered. What is remembered is chosen.
          </p>
        </div>
      </section>

      {/* ═══════════════ FOUNDER ═══════════════ */}
      <section className="relative bg-luxblack-card py-36 md:py-52 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gold/[0.02] to-transparent" />
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 md:gap-20 items-center">
            <div className="section-reveal order-2 md:order-1">
              <p className="text-gold text-xs tracking-mega uppercase mb-8 font-sans">
                ══ المؤسسة ══
              </p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight mb-4">
                <span className="gold-gradient-text">أنيت هورفاث</span>
              </h2>
              <p className="text-gold-light/70 font-sans text-sm mb-12 leading-relaxed tracking-wide">
                مهندسة حسية · مفكرة في الأنظمة
              </p>

              <div className="space-y-6 text-white/75 font-sans text-[15px] leading-[2.2]">
                <p>
                  مبنية من الضرورة الشخصية،<br />
                  لا من نظرية الأعمال.
                </p>
                <p>
                  حين تسببت مركبات العطر الاصطناعية<br />
                  في تفاعلات جلدية مزمنة،<br />
                  فعلت ما لا يفعله معظم الناس:<br />
                  تعلمت العلم،<br />
                  أعادت صياغة المنتج.
                </p>

                <blockquote className="border-r-2 border-gold pr-8 my-12 py-4">
                  <p className="text-gold-light font-serif italic text-lg leading-relaxed">
                    "المواد التي تفهمها تماماً فقط<br />
                    يجب أن تلمس بشرة الإنسان أو هواءه."
                  </p>
                </blockquote>

                <p>
                  تلك القناعة أصبحت Miaamor:<br />
                  عمل شموع صويا مسكوب يدوياً<br />
                  نما من تجربة مطبخ<br />
                  إلى مشروع تجاري مُتحقق منه،<br />
                  مدعوم من المستثمرين<br />
                  بعد ظهور تلفزيوني وطني.
                </p>

                <div className="gold-line w-16 my-8" />

                <p className="text-gold font-medium text-base">
                  ما بنته مع Miaamor كان منتجاً.<br />
                  ما تبنيه الآن هو نظام.
                </p>
              </div>
            </div>

            <div className="section-reveal order-1 md:order-2" style={{ transitionDelay: '0.3s' }}>
              <div className="relative">
                <div className="absolute -inset-4 gold-border pointer-events-none" />
                <div className="absolute -inset-8 border border-gold/10 pointer-events-none" />
                <img
                  src={founderPortrait}
                  alt="Anett Horváth"
                  className="w-full max-w-md mx-auto object-cover shadow-2xl gold-glow"
                  style={{ aspectRatio: '3/4' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              </div>
            </div>
          </div>

          <div className="gold-line w-full mt-24" />
          <p className="section-reveal mt-12 text-center text-gold/40 text-[12px] tracking-luxe font-sans" style={{ transitionDelay: '0.5s' }}>
            Not built from theory. Built from necessity.
          </p>
        </div>
      </section>

      {/* ═══════════════ PRESENCE ═══════════════ */}
      <section className="relative bg-luxblack py-36 md:py-52 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px shimmer-gold" />
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="section-reveal text-center mb-24">
            <p className="text-gold text-xs tracking-mega uppercase mb-8 font-sans">
              ══ الحجم والحضور ══
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight mb-6">
              <span className="gold-gradient-text">تعمل بالفعل على نطاق واسع.</span>
            </h2>
            <div className="gold-line w-32 mx-auto mt-8 mb-8" />
            <p className="text-white/60 font-sans text-base mt-8">
              السوق لا يحتاج إلى الإقناع.<br />لقد استجاب بالفعل.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10 md:gap-12">
            <div className="section-reveal luxury-card p-10 rounded-sm" style={{ transitionDelay: '0.2s' }}>
              <div className="h-px shimmer-gold mb-10" />
              <p className="text-white/80 font-sans text-[15px] leading-[2.2]">
                منتجات Miaamor موجودة<br />
                في مطار فيرنتس ليست الدولي،<br />
                في كلٍّ من SkyCourt<br />
                والطرفية B2،<br />
                البيئة الأولى والأخيرة<br />
                التي يصادفها كل مسافر دولي.
              </p>
            </div>

            <div className="section-reveal luxury-card p-10 rounded-sm" style={{ transitionDelay: '0.4s' }}>
              <div className="h-px shimmer-gold mb-10" />
              <p className="text-white/80 font-sans text-[15px] leading-[2.2]">
                وهي موجودة داخل شبكة تجزئة<br />
                Hungary of Memories،<br />
                أكثر سلاسل تذكارات هنغاريا تقديراً،<br />
                المعترف بها ثماني مرات متتالية<br />
                كالأفضل في فئتها.
              </p>
            </div>

            <div className="section-reveal luxury-card p-10 rounded-sm" style={{ transitionDelay: '0.6s' }}>
              <div className="h-px shimmer-gold mb-10" />
              <p className="text-white/80 font-sans text-[15px] leading-[2.2]">
                وراء الحضور الدائم في التجزئة،<br />
                تم التحقق من العلامة<br />
                عبر أكثر من 100 فعالية مباشرة،<br />
                بما في ذلك مهرجان Sziget،<br />
                وسوق أدفنت كاتدرائية القديس إسطيفان.
              </p>
            </div>
          </div>

          <div className="gold-line w-full mt-24" />
          <p className="section-reveal mt-12 text-center text-gold/40 text-[12px] tracking-luxe font-sans" style={{ transitionDelay: '0.8s' }}>
            The market already responded.
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px shimmer-gold" />
      </section>

      {/* ═══════════════ GLOBAL POSITIONING ═══════════════ */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <img
          src={palaisExterior}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/70" />
        <div className="absolute inset-0 bg-gradient-to-b from-gold/[0.05] to-transparent" />

        {/* Gold frame */}
        <div className="absolute inset-8 md:inset-16 border border-gold/20 pointer-events-none" />

        <div className="relative z-10 w-full max-w-6xl mx-auto px-6 md:px-12 py-32">
          <div className="section-reveal mb-20">
            <p className="text-gold text-xs tracking-mega uppercase mb-8 font-sans">
              ══ التموضع العالمي ══
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-7xl leading-tight mb-6">
              <span className="gold-gradient-text">مُتموضع داخل طبقات القرار.</span>
            </h2>
            <div className="gold-line w-40 mt-8" />
            <p className="text-gold-light/80 font-serif text-lg italic mt-8">
              Palais de la Bourse · Lyon · ECMA
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-12 md:gap-16 mt-24">
            <div className="section-reveal luxury-card p-8 rounded-sm" style={{ transitionDelay: '0.2s' }}>
              <span className="gold-gradient-text text-2xl font-serif font-bold">I</span>
              <h3 className="font-serif text-gold-light text-xl mt-4 mb-6">الوصول الدولي</h3>
              <p className="text-white/70 font-sans text-[14px] leading-[2.2]">
                تواصل نشط جارٍ<br />
                مع مشغّلي المنتجعات الفاخرة،<br />
                والمدراء العامين،<br />
                ومؤسسي الضيافة.
              </p>
              <p className="text-gold/60 font-sans text-xs mt-8 tracking-wide">
                ليس جمهوراً. وصول.
              </p>
            </div>

            <div className="section-reveal luxury-card p-8 rounded-sm" style={{ transitionDelay: '0.4s' }}>
              <span className="gold-gradient-text text-2xl font-serif font-bold">II</span>
              <h3 className="font-serif text-gold-light text-xl mt-4 mb-6">بيئات عالية النية</h3>
              <p className="text-white/70 font-sans text-[14px] leading-[2.2]">
                منتجات مُوضعة<br />
                حيث يشتري مسافرو العالم<br />
                تذكاراتهم الأكثر تعمداً،<br />
                في لحظة المغادرة والوصول.
              </p>
            </div>

            <div className="section-reveal luxury-card p-8 rounded-sm" style={{ transitionDelay: '0.6s' }}>
              <span className="gold-gradient-text text-2xl font-serif font-bold">III</span>
              <h3 className="font-serif text-gold-light text-xl mt-4 mb-6">حضور في فعاليات النخبة</h3>
              <p className="text-white/70 font-sans text-[14px] leading-[2.2]">
                ضيفة مميزة<br />
                في تجمع الرابطة الأوروبية<br />
                لأسواق رأس المال<br />
                داخل Palais de la Bourse في Lyon.
              </p>
            </div>
          </div>

          <div className="gold-line w-full mt-24" />
          <p className="section-reveal mt-12 text-center text-gold/40 text-[12px] tracking-luxe font-sans" style={{ transitionDelay: '0.8s' }}>
            Access is not visibility. It is positioning.
          </p>
        </div>
      </section>

      {/* ═══════════════ SYSTEM ═══════════════ */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <img
          src={systemDesk}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-gold/[0.03] to-transparent" />

        <div className="relative z-10 w-full max-w-5xl mx-auto px-6 md:px-12 py-32">
          <div className="section-reveal">
            <p className="text-gold text-xs tracking-mega uppercase mb-8 font-sans">
              ══ قدرة النظام ══
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-7xl leading-tight mb-6">
              <span className="gold-gradient-text">من الفكرة إلى التنفيذ.</span>
            </h2>
            <p className="text-gold-light/80 font-serif text-xl mt-6 italic">
              بدون وسطاء.
            </p>
            <div className="gold-line w-32 mt-10" />
          </div>

          <div className="section-reveal mt-20 max-w-3xl" style={{ transitionDelay: '0.3s' }}>
            <p className="text-white/75 font-sans text-[16px] leading-[2.2]">
              معظم المستشارين الإبداعيين<br />
              يتوقفون عند المفهوم.
            </p>
            <p className="text-white/75 font-sans text-[16px] leading-[2.2] mt-8">
              Brandhouse AH هي من بين القلائل النادرين<br />
              الذين يستطيعون أخذ نظام علامة<br />
              من الفلسفة<br />
              إلى البنية التحتية الرقمية العاملة،<br />
              بشكل مستقل وسريع.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mt-24">
            <div className="section-reveal luxury-card p-8 text-center" style={{ transitionDelay: '0.4s' }}>
              <span className="gold-gradient-text text-3xl font-serif font-bold">I</span>
              <h3 className="font-serif text-gold-light text-base mt-4">هندسة الأنظمة الرقمية</h3>
            </div>
            <div className="section-reveal luxury-card p-8 text-center" style={{ transitionDelay: '0.5s' }}>
              <span className="gold-gradient-text text-3xl font-serif font-bold">II</span>
              <h3 className="font-serif text-gold-light text-base mt-4">مسارات العمل بالذكاء الاصطناعي</h3>
            </div>
            <div className="section-reveal luxury-card p-8 text-center" style={{ transitionDelay: '0.6s' }}>
              <span className="gold-gradient-text text-3xl font-serif font-bold">III</span>
              <h3 className="font-serif text-gold-light text-base mt-4">تنفيذ سريع</h3>
            </div>
          </div>

          <div className="gold-line w-full mt-24" />
          <p className="section-reveal mt-12 text-center text-gold/40 text-[12px] tracking-luxe font-sans" style={{ transitionDelay: '0.8s' }}>
            Control is the real luxury.
          </p>
        </div>
      </section>

      {/* ═══════════════ PARTNERSHIP ═══════════════ */}
      <section className="relative bg-luxblack py-36 md:py-52 overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px shimmer-gold" />
        <img
          src={palaisBuilding}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-[0.04]"
        />
        
        {/* Gold corner accents */}
        <div className="absolute top-0 left-0 w-40 h-40 border-t-2 border-l-2 border-gold/30" />
        <div className="absolute top-0 right-0 w-40 h-40 border-t-2 border-r-2 border-gold/30" />
        <div className="absolute bottom-0 left-0 w-40 h-40 border-b-2 border-l-2 border-gold/30" />
        <div className="absolute bottom-0 right-0 w-40 h-40 border-b-2 border-r-2 border-gold/30" />

        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12">
          <div className="section-reveal text-center mb-8">
            <p className="text-gold text-xs tracking-mega uppercase mb-8 font-sans">
              ══ نموذج الشراكة ══
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight mb-8">
              <span className="gold-gradient-text">ثلاث طرق للعمل معاً.</span>
            </h2>
            <div className="gold-line w-32 mx-auto my-8" />
            <p className="text-white/60 font-sans text-base leading-loose max-w-xl mx-auto">
              كل تعاون يُناقش بشكل خاص.<br />
              لا قوالب. لا هياكل جاهزة من الرف.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-10 md:gap-12 mt-24">
            <div className="section-reveal luxury-card p-10 text-center rounded-sm" style={{ transitionDelay: '0.2s' }}>
              <div className="h-px shimmer-gold mb-10" />
              <span className="text-gold text-xs font-sans tracking-mega">المستوى الأول</span>
              <h3 className="font-serif text-gold-light text-xl mt-6 mb-4">التكامل التحريري</h3>
              <div className="gold-line w-12 mx-auto mt-6" />
            </div>
            <div className="section-reveal luxury-card p-10 text-center rounded-sm gold-border-thick" style={{ transitionDelay: '0.4s' }}>
              <div className="h-px shimmer-gold mb-10" />
              <span className="text-gold text-xs font-sans tracking-mega">المستوى الثاني</span>
              <h3 className="font-serif text-gold-light text-xl mt-6 mb-4">تصميم النظام الحسي</h3>
              <div className="gold-line w-12 mx-auto mt-6" />
            </div>
            <div className="section-reveal luxury-card p-10 text-center rounded-sm" style={{ transitionDelay: '0.6s' }}>
              <div className="h-px shimmer-gold mb-10" />
              <span className="text-gold text-xs font-sans tracking-mega">المستوى الثالث</span>
              <h3 className="font-serif text-gold-light text-xl mt-6 mb-4">الهندسة الإدراكية الكاملة</h3>
              <div className="gold-line w-12 mx-auto mt-6" />
            </div>
          </div>

          <div className="gold-line w-full mt-24" />
          <p className="section-reveal mt-12 text-center text-gold/40 text-[12px] tracking-luxe font-sans" style={{ transitionDelay: '0.8s' }}>
            Not scalable. Selective.
          </p>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px shimmer-gold" />
      </section>

      {/* ═══════════════ CLOSING ═══════════════ */}
      <section className="relative bg-black py-36 md:py-52 overflow-hidden">
        <img
          src={handshakeLuxury}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-[0.06]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-gold/[0.03] to-transparent" />
        
        {/* Gold frame */}
        <div className="absolute inset-10 md:inset-20 border border-gold/15 pointer-events-none" />

        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="section-reveal">
            <img src={monogramAH} alt="AH" className="h-16 md:h-20 mx-auto mb-12 monogram-glow float-gold" />
            <p className="text-gold text-xs tracking-mega uppercase mb-10 font-sans">
              ══ المحادثة ══
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-7xl leading-tight mb-12">
              <span className="gold-gradient-text">إذا كان هذا<br />يتوافق.</span>
            </h2>
          </div>

          <div className="section-reveal space-y-8 text-white/75 font-sans text-[16px] leading-[2.2]" style={{ transitionDelay: '0.3s' }}>
            <p>
              إذا كان عقارك يقدّر<br />
              الحرفية،<br />
              الدقة العاطفية،<br />
              وفهم أن كيفية شعور الضيوف عند الوصول<br />
              تحدد كل ما يليه،<br />
              فيجب أن نتحدث.
            </p>
            <p className="text-white/50 text-sm mt-10">
              نختار تعاملاً واحداً في كل مرة،<br />
              وفقط عندما يكون التوافق حقيقياً.
            </p>
          </div>

          <div className="section-reveal mt-24" style={{ transitionDelay: '0.5s' }}>
            <div className="gold-line w-24 mx-auto mb-10" />
            <p className="text-gold/60 text-xs tracking-luxe uppercase mb-6 font-sans">تواصل</p>
            <a
              href="mailto:anett@ahbrandhouse.com"
              className="inline-block px-12 py-5 gold-border-thick text-gold font-serif text-xl md:text-2xl hover:bg-gold/10 transition-all duration-700 tracking-wide"
            >
              anett@ahbrandhouse.com
            </a>
            <p className="text-white/30 text-xs tracking-wide mt-10 font-sans">
              استشارة خاصة · نقاش شراكة سري
            </p>
          </div>

          <div className="section-reveal mt-32 pt-16 border-t border-gold/20" style={{ transitionDelay: '0.7s' }}>
            <img src={monogramAH} alt="AH" className="h-10 mx-auto opacity-60 mb-8 monogram-glow" />
            <p className="text-gold/40 text-[12px] tracking-mega font-sans uppercase">
              AH BRANDHOUSE · MMXXVI
            </p>
          </div>

          <p className="section-reveal mt-20 text-gold/25 text-[12px] tracking-luxe font-sans italic" style={{ transitionDelay: '0.9s' }}>
            If this resonates, there is a reason.
          </p>
        </div>
      </section>
    </div>
  )
}
